import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { api, getUser, setUser as storeUser } from '../lib/api';
import ReferralCard from '../components/ReferralCard';

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [profilePassword, setProfilePassword] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  useEffect(() => {
    if (!getUser()) { navigate('/login?next=/account'); return; }
    api('/api/me')
      .then((d) => {
        setUser(d.user);
        setFullName(d.user.full_name || '');
        setEmail(d.user.email || '');
      })
      .catch(() => navigate('/login?next=/account'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileBusy(true);
    try {
      const body = {};
      if (fullName.trim() !== user.full_name) body.full_name = fullName.trim();
      if (email.trim().toLowerCase() !== user.email) body.email = email.trim();
      if (!Object.keys(body).length) { setProfileMsg({ type: 'info', text: 'No changes to save.' }); return; }
      if (body.email) {
        if (!profilePassword) { setProfileMsg({ type: 'err', text: 'Enter your current password to change email.' }); return; }
        body.current_password = profilePassword;
      }
      await api('/api/me', { method: 'PATCH', body });
      setProfilePassword('');
      const updated = { ...user, ...body };
      setUser(updated);
      storeUser(updated);
      setProfileMsg({ type: 'ok', text: body.email ? 'Saved. Please verify your new email address.' : 'Saved.' });
    } catch (err) {
      const code = err.body?.error;
      const msg = code === 'no_changes' ? 'No changes to save.'
        : code === 'wrong_password' ? 'Password is incorrect.'
        : code === 'email_in_use' ? 'That email is already in use.'
        : code === 'password_required_for_email_change' ? 'Enter your current password to change email.'
        : 'Could not save. Try again.';
      setProfileMsg({ type: 'err', text: msg });
    } finally {
      setProfileBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPassword.length < 8 || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) { setPwMsg({ type: 'err', text: 'Password must be 8+ characters with uppercase, lowercase, and a number.' }); return; }
    if (newPassword !== confirmPassword) { setPwMsg({ type: 'err', text: 'Passwords do not match.' }); return; }
    setPwBusy(true);
    try {
      await api('/api/auth/change-password', { method: 'POST', body: { current_password: currentPassword, new_password: newPassword } });
      setPwMsg({ type: 'ok', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const code = err.body?.error;
      setPwMsg({ type: 'err', text: code === 'wrong_password' ? 'Current password is incorrect.' : 'Could not update password.' });
    } finally {
      setPwBusy(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-slate-500">Loading...</div>;

  const inputCls = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand';

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold mb-8">Account settings</h1>

      <ReferralCard />

      {/* Profile section */}
      <form onSubmit={saveProfile} className="space-y-4 mb-10">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Profile</h2>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Full name</span>
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} maxLength={120} required />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
        </label>
        {user && email.trim().toLowerCase() !== user.email && (
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Current password <span className="font-normal text-slate-400">(required to change email)</span></span>
            <input type="password" value={profilePassword} onChange={(e) => setProfilePassword(e.target.value)} className={inputCls} />
          </label>
        )}
        {profileMsg && <Msg {...profileMsg} />}
        <button type="submit" disabled={profileBusy} className="bg-brand text-white text-sm px-5 py-2 rounded-md font-semibold hover:bg-brand-dark disabled:opacity-50 inline-flex items-center gap-2">
          {profileBusy && <Loader2 size={14} className="animate-spin" />} Save changes
        </button>
      </form>

      {/* Password section */}
      <form onSubmit={changePassword} className="space-y-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Change password</h2>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Current password</span>
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputCls} required />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">New password</span>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} minLength={8} required />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</span>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls} required />
        </label>
        {pwMsg && <Msg {...pwMsg} />}
        <button type="submit" disabled={pwBusy} className="bg-slate-800 text-white text-sm px-5 py-2 rounded-md font-semibold hover:bg-slate-900 disabled:opacity-50 inline-flex items-center gap-2">
          {pwBusy && <Loader2 size={14} className="animate-spin" />} Update password
        </button>
      </form>
    </div>
  );
}

function Msg({ type, text }) {
  const styles = {
    ok:   'bg-emerald-50 border-emerald-200 text-emerald-700',
    err:  'bg-rose-50 border-rose-200 text-rose-700',
    info: 'bg-slate-50 border-slate-200 text-slate-600',
  };
  const Icon = type === 'ok' ? CheckCircle2 : AlertCircle;
  return (
    <div className={`px-3 py-2 border rounded text-sm flex items-start gap-2 ${styles[type] || styles.info}`}>
      <Icon size={14} className="shrink-0 mt-0.5" /> {text}
    </div>
  );
}
